import { getCart, updateItem, addItem } from '@nutima-themes/utils/cart'
import { EVENTS, subscribe } from '@nutima-themes/utils/pubsub'
import 'components/quantity-picker'

class DrawerCart extends HTMLElement {
  constructor() {
    super()

    this.drawerWrapper = this.querySelector('.drawer-cart')
    this.sectionId = this.id
  }

  async connectedCallback() {
    this.setupEventListeners()
    this.initializeCart()
  }

  async initializeCart() {
    this.querySelectorAll('.drawer-cart__item').forEach((item) => {
      this.handleRemoveItem(item)
    })
  }

  setupEventListeners() {
    subscribe(EVENTS.quantityChange, ({ detail }) => {
      const sections = detail.cartType === 'drawer' ? this.sectionId : undefined
      // Use line item key if available, fallback to variant ID
      const itemId = detail.lineItemKey || detail.variantId
      
      // Ensure we have a valid item ID
      if (!itemId) {
        console.error('DrawerCart: No valid item ID found for quantity update')
        return
      }
      
      updateItem(itemId, detail.quantity, sections, detail.productHandle)
    })

    subscribe(EVENTS.cartChange, ({ detail }) => {
      this.querySelector('.drawer-cart').classList.remove('loading')
      if (detail.cart && detail.cart.sections && detail.cart.sections[this.sectionId]) {
        this.renderCart(detail.cart)
      }
    })

    subscribe(EVENTS.cartBeforeChange, ({ detail }) => {
      this.querySelector('.drawer-cart').classList.add('loading')
    })

    subscribe(EVENTS.cartError, ({ detail }) => {
      this.querySelector('.drawer-cart').classList.remove('loading')
    })

    subscribe(EVENTS.drawerOpen, () => this.openDrawer())
    subscribe(EVENTS.drawerClose, () => this.closeDrawer())
    subscribe(EVENTS.drawerToggle, () => this.toggleDrawer())

    this.addEventListener('click', (e) => {
      if (
        e.target.closest('.drawer-cart__close') ||
        e.target.closest('.drawer-cart__cover') ||
        e.target.closest('.drawer-cart__continue')
      ) {
        this.closeDrawer()
      }
    })

    document.querySelectorAll('[data-drawer-cart-toggle]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault()
        this.toggleDrawer()
      })
    })
  }

  updateAttribute(oldElement, newElement, attr) {
    if (oldElement && newElement) {
      oldElement.setAttribute(attr, newElement.getAttribute(attr))
    }
  }

  handleRemoveItem(item){
    const removeButton = item.querySelector('.drawer-cart__remove-item')
    if (removeButton) {
      // Remove any existing event listeners by cloning the button
      const newRemoveButton = removeButton.cloneNode(true)
      removeButton.parentNode.replaceChild(newRemoveButton, removeButton)
      
      newRemoveButton.addEventListener('click', (e) => {
        e.preventDefault()
        const quantityEl = item.querySelector('.quantity-picker__input')
        
        if (quantityEl) {
          quantityEl.value = 0
          quantityEl.dispatchEvent(new Event('change'))
        }
      })
    }
  }

  renderCart(cart) {
    const parser = new DOMParser()
    const cartElement = parser.parseFromString(cart.sections[this.sectionId], 'text/html')
    const classUpdateList = [
      '.drawer-cart__items',
      '.drawer-cart__empty',
      '.drawer-cart__footer-buttons',
      '.drawer-cart__summary',
      '.drawer-cart__shop-now',
      '.drawer-cart__recommendation'
    ]

    classUpdateList.forEach((selector) => {
      if (this.querySelector(selector) && cartElement.querySelector(selector))
        this.updateAttribute(this.querySelector(selector), cartElement.querySelector(selector), 'class')
    })
    this.querySelector('.drawer-cart__total-price').textContent =
      cartElement.querySelector('.drawer-cart__total-price').textContent
      
    this.querySelector('.drawer-cart__title').textContent =
      cartElement.querySelector('.drawer-cart__title').textContent


    // Smart cart update: preserve images but maintain correct order
    const currentItemsContainer = this.querySelector('.drawer-cart__items')
    const newItemsFromAPI = cartElement.querySelectorAll('.drawer-cart__item')
    const currentItems = this.querySelectorAll('.drawer-cart__item')
    
    if (currentItemsContainer && newItemsFromAPI.length > 0) {
      // Create a map of current items by line item key for fast lookup
      const currentItemsMap = new Map()
      currentItems.forEach(item => {
        const key = item.dataset.lineItemKey
        if (key) currentItemsMap.set(key, item)
      })
      
      // Process items in the correct order from API response
      newItemsFromAPI.forEach((newItem, index) => {
        const lineItemKey = newItem.dataset.lineItemKey
        const existingItem = currentItemsMap.get(lineItemKey)
        
        if (existingItem) {
          // Update existing item content (preserving images)
          this.updateExistingItem(existingItem, newItem)
          
          // Move to correct position if needed
          const currentPosition = Array.from(currentItemsContainer.children).indexOf(existingItem)
          if (currentPosition !== index) {
            if (index === currentItemsContainer.children.length - 1) {
              currentItemsContainer.appendChild(existingItem)
            } else {
              currentItemsContainer.insertBefore(existingItem, currentItemsContainer.children[index])
            }
          }
          
          // Remove from map so we know it's been processed
          currentItemsMap.delete(lineItemKey)
        } else {
          // New item - add it at the correct position
          const clonedNewItem = newItem.cloneNode(true)
          if (index === currentItemsContainer.children.length) {
            currentItemsContainer.appendChild(clonedNewItem)
          } else {
            currentItemsContainer.insertBefore(clonedNewItem, currentItemsContainer.children[index])
          }
          this.handleRemoveItem(clonedNewItem)
        }
      })
      
      // Remove any items that are no longer in the cart
      currentItemsMap.forEach(item => item.remove())
    }
  }

  updateExistingItem(existingItem, newItem) {
    // Update quantity input (preserving the image)
    const existingQuantityInput = existingItem.querySelector('.quantity-picker__input')
    const newQuantityInput = newItem.querySelector('.quantity-picker__input')
    if (existingQuantityInput && newQuantityInput) {
      existingQuantityInput.value = newQuantityInput.value
    }

    // Update selling plan name (first p tag in the top section)
    const existingSellingPlan = existingItem.querySelector('div:first-child p:first-child')
    const newSellingPlan = newItem.querySelector('div:first-child p:first-child')
    if (existingSellingPlan && newSellingPlan) {
      existingSellingPlan.textContent = newSellingPlan.textContent
    }

    // Update price (in the same top section)
    const existingPrice = existingItem.querySelector('.product-price-on-sale')
    const newPrice = newItem.querySelector('.product-price-on-sale')
    if (existingPrice && newPrice) {
      existingPrice.innerHTML = newPrice.innerHTML
    }

    // Update button states
    this.updateAttribute(
      existingItem.querySelector('.quantity-picker__trash'),
      newItem.querySelector('.quantity-picker__trash'),
      'class'
    )
    this.updateAttribute(
      existingItem.querySelector('.quantity-picker__minus'),
      newItem.querySelector('.quantity-picker__minus'),
      'class'
    )
    
    // Update any data attributes that might have changed
    if (newItem.dataset.variantId) {
      existingItem.dataset.variantId = newItem.dataset.variantId
    }
    
    // Re-attach event listeners for this item
    this.handleRemoveItem(existingItem)
  }

  openDrawer() {
    if (window.location.pathname === '/cart') return
    this.drawerWrapper.classList.add('drawer-cart--open')
    window.dispatchEvent(new CustomEvent('drawer:open'))
  }

  closeDrawer() {
    this.drawerWrapper.classList.remove('drawer-cart--open')
  }

  toggleDrawer() {
    if (window.location.pathname === '/cart') return
    this.drawerWrapper.classList.toggle('drawer-cart--open')
    window.dispatchEvent(new CustomEvent('drawer:open'))
  }
}

customElements.define('n-drawer-cart', DrawerCart)
